import { useCallback, useEffect, useRef, useState } from 'react';
import TensaiService from 'src/services/tensai';
import { HttpStatus } from 'src/util/http/statusCodes';
import useFlashMessage from 'src/hooks/FlashMessage';
import {
  confirmLgpdSignature,
  destroySession,
} from 'src/stores/actions/access';
import { ERROR_MESSAGES } from '../constants';

const RETRY_DELAY_MS = 200;
const tensaiService = new TensaiService();

/**
 * Orquestra o fluxo de assinatura LGPD (espelha o LgpdSignatureService do
 * tensai-front): busca os documentos pendentes, controla o modo da modal
 * (aceite/rejeição), executa a assinatura e baixa a flag no Redux ao concluir.
 *
 * @param {boolean} needSignDocuments flag de pendência vinda do estado `access`
 * @param {Function} dispatch dispatch do Redux
 */
export default function useLgpdSignature(needSignDocuments, dispatch) {
  const [renderMessage] = useFlashMessage();
  const [documents, setDocuments] = useState([]);
  const [isSigning, setIsSigning] = useState(false);
  // 'accept' = aceite/discordo | 'reject' = confirmação de rejeição
  const [mode, setMode] = useState('accept');

  const retryTimeoutRef = useRef(null);

  // logout: derruba o token e força o retorno ao login
  const logout = useCallback(() => {
    destroySession(dispatch);
  }, [dispatch]);

  // GET dos documentos pendentes — retry em 4xx, logout em 5xx (doc §3.3)
  const fetchDocuments = useCallback(() => {
    tensaiService
      .getToSignLgpdDocuments()
      .then((response) => {
        setDocuments(response.data || []);
      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status >= HttpStatus.INTERNAL_SERVER_ERROR) {
          renderMessage(
            ERROR_MESSAGES.getDocumentsServer.title,
            ERROR_MESSAGES.getDocumentsServer.message,
            'error',
          );
          logout();
          return;
        }
        renderMessage(
          ERROR_MESSAGES.getDocumentsClient.title,
          ERROR_MESSAGES.getDocumentsClient.message,
          'error',
        );
        retryTimeoutRef.current = setTimeout(fetchDocuments, RETRY_DELAY_MS);
      });
  }, [logout, renderMessage]);

  // Dispara a busca quando há pendência; limpa retry pendente ao desmontar
  useEffect(() => {
    if (!needSignDocuments) return undefined;
    setMode('accept');
    fetchDocuments();
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [needSignDocuments, fetchDocuments]);

  // 'Concordo' → POST de assinatura. Em sucesso, o interceptor já rotacionou o
  // JWT; confirmamos a assinatura no Redux para baixar a flag e fechar a modal.
  const onConfirm = useCallback(() => {
    const ids = documents.map((doc) => doc.id);
    setIsSigning(true);
    tensaiService
      .signLgpdDocuments(ids)
      .then(() => {
        confirmLgpdSignature(dispatch);
      })
      .catch(() => {
        setIsSigning(false);
        renderMessage(
          ERROR_MESSAGES.signatures.title,
          ERROR_MESSAGES.signatures.message,
          'error',
        );
      });
  }, [documents, dispatch, renderMessage]);

  // 'Discordo' → confirmação de rejeição
  const onDiscord = useCallback(() => setMode('reject'), []);
  // 'Cancelar' (no modo rejeição) → volta ao aceite
  const onRejectCancel = useCallback(() => setMode('accept'), []);
  // 'Rejeitar' (no modo rejeição) → logout
  const onRejectConfirm = useCallback(() => logout(), [logout]);

  return {
    documents,
    isSigning,
    mode,
    onConfirm,
    onDiscord,
    onRejectCancel,
    onRejectConfirm,
  };
}
